import qs from 'qs'
import { metersToMiles } from '@lib/conversions'
import {
  PGA_COACH_DISTANCE_OPTIONS,
  PGA_COACH_PROGRAMS_SEARCH_PATHNAME,
} from './constants'
import {
  buildIndexableCoachSearchPathname,
  buildJourneySearchUrl,
  normalizeJourneySearchState,
} from './search-state'

/** Algolia group-events tag → journey persona id (legacy things-to-do tiles). */
export const THINGS_TO_DO_TAG_TO_PERSONA = {
  'PGA HOPE': 'military-veterans',
  'PGA Jr. League': 'juniors',
  'PGA Family Cup': 'juniors',
  'Beginner Friendly': 'start-golf',
  'On the Course': 'play-better',
  'Women Only': 'womens-golf',
  Military: 'military-veterans',
  Advanced: 'play-better',
}

const JOURNEY_DISTANCE_IDS = PGA_COACH_DISTANCE_OPTIONS.map((o) => Number(o.id))

const asArray = (value) => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

const normalizeTags = (ttdState) => {
  const tags = ttdState?.refinementList?.tags
  return asArray(tags)
    .map((tag) => (tag == null ? '' : String(tag).trim()))
    .filter(Boolean)
}

const resolvePersonaFromTags = (tags) => {
  for (let i = 0; i < tags.length; i += 1) {
    const persona = THINGS_TO_DO_TAG_TO_PERSONA[tags[i]]
    if (persona) return persona
  }
  return ''
}

const snapMetersToJourneyDistanceId = (rawMeters) => {
  const meters = Number(rawMeters)
  if (!Number.isFinite(meters) || meters <= 0) return null
  const miles = metersToMiles(meters)
  let closestId = JOURNEY_DISTANCE_IDS[0]
  let closestDelta = Infinity
  JOURNEY_DISTANCE_IDS.forEach((id) => {
    const delta = Math.abs(miles - id)
    if (delta < closestDelta) {
      closestDelta = delta
      closestId = id
    }
  })
  return String(closestId)
}

const mapPriceFromMultiRange = (multiRange) => {
  const raw = multiRange?.total_price_in_cents
  if (raw == null || raw === '') return null
  const normalized = String(raw).replace(/^\$/, '')
  const end = Number(normalized.split(':').pop())
  if (!Number.isFinite(end)) return null
  if (end <= 0) return 'free'
  if (end <= 2500) return 'under-50'
  if (end <= 5000) return 'under-50'
  if (end <= 10000) return 'under-100'
  return '100-plus'
}

export const parseThingsToDoSearchQuery = (searchString = '') => {
  const trimmed = String(searchString || '').replace(/^\?/, '')
  if (!trimmed) return {}
  const parsed = qs.parse(trimmed)
  const { configure: _configure, ...rest } = parsed
  return rest
}

export const mapThingsToDoSearchStateToJourneyState = (ttdState = {}) => {
  const tags = normalizeTags(ttdState)
  const persona = resolvePersonaFromTags(tags)
  const distance = snapMetersToJourneyDistanceId(ttdState.aroundRadius)
  const price = mapPriceFromMultiRange(ttdState.multiRange)

  const journeyState = normalizeJourneySearchState({
    ...(ttdState.query && { query: String(ttdState.query) }),
    ...(ttdState.aroundLatLng && { aroundLatLng: String(ttdState.aroundLatLng) }),
    ...(ttdState.placeName && { placeName: String(ttdState.placeName) }),
    ...(persona && { persona }),
    ...(distance && { distance }),
    ...(price && { price }),
  })

  return journeyState
}

const resolveProgramsPathname = (pathname = '') => {
  const normalized = String(pathname || '').replace(/\/$/, '') || '/'
  if (normalized === '/things-to-do' || normalized === '/things-to-do/search') {
    return PGA_COACH_PROGRAMS_SEARCH_PATHNAME
  }
  const cityMatch = normalized.match(/^\/things-to-do\/([^/]+)\/([^/]+)$/)
  if (cityMatch && cityMatch[1] !== 'events') {
    return `${PGA_COACH_PROGRAMS_SEARCH_PATHNAME}/${cityMatch[1]}/${cityMatch[2]}`
  }
  return null
}

export const isThingsToDoProgramsRedirectPath = (pathname = '') => (
  resolveProgramsPathname(pathname) != null
)

const resolveThingsToDoRedirectTargetPathname = (sourcePathname, journeyState) => {
  const indexablePathname = buildIndexableCoachSearchPathname(journeyState)
  if (indexablePathname !== PGA_COACH_PROGRAMS_SEARCH_PATHNAME) {
    return indexablePathname
  }
  return resolveProgramsPathname(sourcePathname)
}

export const buildThingsToDoProgramsRedirectLocation = ({
  pathname = '',
  search = '',
} = {}) => {
  if (!resolveProgramsPathname(pathname)) return null
  const ttdState = parseThingsToDoSearchQuery(search)
  const journeyState = mapThingsToDoSearchStateToJourneyState(ttdState)
  const targetPathname = resolveThingsToDoRedirectTargetPathname(pathname, journeyState)
  return buildJourneySearchUrl(journeyState, targetPathname)
}

/** When legacy refinementList params land on /golf-lessons, map to canonical journey URL. */
export const buildLegacyProgramsSearchRedirectLocation = ({
  pathname = '',
  search = '',
} = {}) => {
  const ttdState = parseThingsToDoSearchQuery(search)
  const hasLegacyRefinement = Boolean(
    ttdState.refinementList
    && typeof ttdState.refinementList === 'object'
    && Object.keys(ttdState.refinementList).length > 0,
  )
  if (!hasLegacyRefinement) return null
  const journeyState = mapThingsToDoSearchStateToJourneyState(ttdState)
  const targetPathname = resolveThingsToDoRedirectTargetPathname(
    pathname || PGA_COACH_PROGRAMS_SEARCH_PATHNAME,
    journeyState,
  ) || PGA_COACH_PROGRAMS_SEARCH_PATHNAME
  return buildJourneySearchUrl(journeyState, targetPathname)
}
